import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Grid3X3,
  CalendarDays,
  AlertCircle,
  Loader2,
  User,
  Moon,
} from 'lucide-react';
import { scheduleService, ScheduleEntry, ScheduleSession, DAY_OF_WEEK_LABELS, RamadanPeriod } from '@/features/calendar/services/scheduleService';
import { fetchChildSchedule } from '@/features/parent/services/parentService';

type ViewMode = 'weekly' | 'monthly';

// Entry with session info for monthly view
interface EntryWithSession {
  entry: ScheduleEntry;
  session: ScheduleSession;
}

// Time slots from 8:00 to 21:00
const TIME_SLOTS = Array.from({ length: 14 }, (_, i) => {
  const hour = 8 + i;
  return `${hour.toString().padStart(2, '0')}:00`;
});

// Days Monday (1) to Sunday (0) - full week
const DAYS = [1, 2, 3, 4, 5, 6, 0];

// Row height in pixels
const ROW_HEIGHT = 44;

// Helper to format date as YYYY-MM-DD in local time
const formatLocalDate = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

interface MySchedulePageProps {
  /** When set (parent panel), loads this child's schedule instead of the logged-in student's */
  studentId?: string;
}

const MySchedulePage: React.FC<MySchedulePageProps> = ({ studentId }) => {
  const [entries, setEntries] = useState<ScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('weekly');
  const [ramadanPeriod, setRamadanPeriod] = useState<RamadanPeriod | null>(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    return formatLocalDate(monday);
  });
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  // Fetch student schedule and Ramadan period
  const loadSchedule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let startDate: string;
      let endDate: string;

      if (viewMode === 'weekly') {
        // Calculate week end date for blocked dates API
        const weekStartDate = new Date(currentWeekStart);
        const weekEndDate = new Date(weekStartDate);
        weekEndDate.setDate(weekEndDate.getDate() + 6);
        startDate = currentWeekStart;
        endDate = formatLocalDate(weekEndDate);
      } else {
        // For monthly view, fetch the entire month
        const firstDayOfMonth = new Date(currentMonth.year, currentMonth.month, 1);
        const lastDayOfMonth = new Date(currentMonth.year, currentMonth.month + 1, 0);
        startDate = formatLocalDate(firstDayOfMonth);
        endDate = formatLocalDate(lastDayOfMonth);
      }

      // Fetch schedule and blocked dates (which includes Ramadan info) in parallel
      const [scheduleData, blockedDatesData] = await Promise.all([
        studentId
          ? (fetchChildSchedule(studentId, startDate, endDate) as Promise<ScheduleEntry[]>)
          : scheduleService.getStudentSchedule(startDate, endDate),
        // Try to get blocked dates - this includes Ramadan period
        scheduleService.getBlockedDates('', startDate, endDate).catch(() => null),
      ]);

      // Filter entries that have sessions for the current week
      const entriesWithSessions = Array.isArray(scheduleData) ? scheduleData : (scheduleData as any).entries || [];
      setEntries(entriesWithSessions);

      // Set Ramadan period if available
      if (blockedDatesData?.ramadan) {
        setRamadanPeriod(blockedDatesData.ramadan);
      }
    } catch (err) {
      console.error('Failed to load schedule:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart, viewMode, currentMonth, studentId]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  // Helper to parse currentWeekStart string as local date
  const parseWeekStart = () => {
    const [year, month, day] = currentWeekStart.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Week navigation
  const handlePreviousWeek = () => {
    const current = parseWeekStart();
    current.setDate(current.getDate() - 7);
    setCurrentWeekStart(formatLocalDate(current));
  };

  const handleNextWeek = () => {
    const current = parseWeekStart();
    current.setDate(current.getDate() + 7);
    setCurrentWeekStart(formatLocalDate(current));
  };

  const handleToday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    setCurrentWeekStart(formatLocalDate(monday));
  };

  // Month navigation
  const handlePreviousMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { year: prev.year, month: prev.month - 1 };
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: prev.month + 1 };
    });
  };

  const formatWeekRange = () => {
    const start = parseWeekStart();
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
    return `${start.toLocaleDateString('fr-FR', options)} - ${end.toLocaleDateString('fr-FR', options)}`;
  };

  const formatMonth = () => {
    const date = new Date(currentMonth.year, currentMonth.month);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  // Helper functions for grid
  const getDateForDay = useCallback((dayOfWeek: number) => {
    const weekStartDate = parseWeekStart();
    const targetDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    const daysToAdd = targetDay - 1;
    const resultDate = new Date(weekStartDate);
    resultDate.setDate(resultDate.getDate() + daysToAdd);
    return resultDate;
  }, [currentWeekStart]);

  const isToday = useCallback((dayOfWeek: number) => {
    const today = new Date();
    const date = getDateForDay(dayOfWeek);
    return (
      today.getDate() === date.getDate() &&
      today.getMonth() === date.getMonth() &&
      today.getFullYear() === date.getFullYear()
    );
  }, [getDateForDay]);

  // Check if a specific day is during Ramadan
  const isRamadanDay = useCallback((dayOfWeek: number): boolean => {
    if (!ramadanPeriod) return false;
    const date = getDateForDay(dayOfWeek);
    const dateStr = formatLocalDate(date);
    return dateStr >= ramadanPeriod.startDate && dateStr <= ramadanPeriod.endDate;
  }, [getDateForDay, ramadanPeriod]);

  // Get entries for a specific day and time slot
  const getEntriesForDayAndTime = useCallback((dayOfWeek: number, timeSlot: string) => {
    const slotHour = parseInt(timeSlot.split(':')[0]);
    const dateForDay = getDateForDay(dayOfWeek);
    const dateStr = formatLocalDate(dateForDay);

    return entries.filter((entry) => {
      // Match on the cell's concrete date, not entry.dayOfWeek — rescheduled
      // (exception) sessions can land on a different weekday than their entry
      if (entry.sessions && entry.sessions.length > 0) {
        const sessionForDate = entry.sessions.find(s => {
          const sessionDateStr = typeof s.date === 'string'
            ? s.date.split('T')[0]
            : formatLocalDate(new Date(s.date));
          return sessionDateStr === dateStr && !s.isCancelled;
        });

        if (!sessionForDate) return false;

        const sessionStartHour = parseInt(sessionForDate.startTime.split(':')[0]);
        return sessionStartHour === slotHour;
      }

      // Fallback to entry time (no sessions data): keep weekday matching
      if (entry.dayOfWeek !== dayOfWeek) return false;
      const startHour = parseInt(entry.startTime.split(':')[0]);
      return startHour === slotHour;
    });
  }, [entries, getDateForDay]);

  // Calculate entry height
  const getEntryHeight = useCallback((entry: ScheduleEntry, dateForDay: Date) => {
    const dateStr = formatLocalDate(dateForDay);
    let startTime = entry.startTime;
    let endTime = entry.endTime;

    if (entry.sessions && entry.sessions.length > 0) {
      const session = entry.sessions.find(s => {
        const sessionDateStr = typeof s.date === 'string'
          ? s.date.split('T')[0]
          : formatLocalDate(new Date(s.date));
        return sessionDateStr === dateStr && !s.isCancelled;
      });
      if (session) {
        startTime = session.startTime;
        endTime = session.endTime;
      }
    }

    const startParts = startTime.split(':');
    const endParts = endTime.split(':');
    const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
    const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
    const durationHours = (endMinutes - startMinutes) / 60;
    return durationHours * ROW_HEIGHT;
  }, []);

  // Calculate top offset
  const getEntryTopOffset = useCallback((entry: ScheduleEntry, dateForDay: Date) => {
    const dateStr = formatLocalDate(dateForDay);
    let startTime = entry.startTime;

    if (entry.sessions && entry.sessions.length > 0) {
      const session = entry.sessions.find(s => {
        const sessionDateStr = typeof s.date === 'string'
          ? s.date.split('T')[0]
          : formatLocalDate(new Date(s.date));
        return sessionDateStr === dateStr && !s.isCancelled;
      });
      if (session) {
        startTime = session.startTime;
      }
    }

    const startParts = startTime.split(':');
    const minutes = parseInt(startParts[1]);
    return (minutes / 60) * ROW_HEIGHT;
  }, []);

  // Get display time for entry
  const getDisplayTimes = useCallback((entry: ScheduleEntry, dateForDay: Date) => {
    const dateStr = formatLocalDate(dateForDay);

    if (entry.sessions && entry.sessions.length > 0) {
      const session = entry.sessions.find(s => {
        const sessionDateStr = typeof s.date === 'string'
          ? s.date.split('T')[0]
          : formatLocalDate(new Date(s.date));
        return sessionDateStr === dateStr && !s.isCancelled;
      });
      if (session) {
        return { startTime: session.startTime, endTime: session.endTime };
      }
    }

    return { startTime: entry.startTime, endTime: entry.endTime };
  }, []);

  // Group entries by date for monthly view - store session-specific info
  const entriesByDate = useMemo(() => {
    const map = new Map<string, EntryWithSession[]>();
    entries.forEach(entry => {
      if (entry.sessions) {
        entry.sessions.forEach(session => {
          if (!session.isCancelled) {
            const dateStr = typeof session.date === 'string'
              ? session.date.split('T')[0]
              : formatLocalDate(new Date(session.date));
            const existing = map.get(dateStr) || [];
            // Check if this entry is already added for this date
            if (!existing.find(e => e.entry.id === entry.id)) {
              existing.push({ entry, session });
            }
            map.set(dateStr, existing);
          }
        });
      }
    });
    return map;
  }, [entries]);

  // Render weekly grid view
  const renderWeeklyGrid = () => (
    <div className="overflow-auto flex-1 border border-gray-200 rounded-xl bg-white">
      <div className="min-w-[900px]">
        {/* Header */}
        <div className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <div className="p-2 text-center text-xs font-medium text-gray-500 border-r border-gray-200">
            Heure
          </div>
          {DAYS.map((dayOfWeek) => {
            const date = getDateForDay(dayOfWeek);
            const today = isToday(dayOfWeek);
            const isRamadan = isRamadanDay(dayOfWeek);
            return (
              <div
                key={dayOfWeek}
                className={`p-2 text-center border-l border-gray-200 ${
                  isRamadan
                    ? 'bg-emerald-50'
                    : today
                    ? 'bg-blue-50'
                    : ''
                }`}
              >
                <p className={`text-xs font-medium ${
                  isRamadan
                    ? 'text-emerald-700'
                    : today
                    ? 'text-blue-700'
                    : 'text-gray-500'
                }`}>
                  {DAY_OF_WEEK_LABELS[dayOfWeek]}
                  {isRamadan && (
                    <Moon size={10} className="inline-block ml-1 text-emerald-600" />
                  )}
                </p>
                <p className={`text-lg font-bold ${
                  isRamadan
                    ? 'text-emerald-900'
                    : today
                    ? 'text-blue-900'
                    : 'text-gray-900'
                }`}>
                  {date.getDate()}
                </p>
                <p className={`text-xs ${
                  isRamadan
                    ? 'text-emerald-600'
                    : today
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}>
                  {date.toLocaleDateString('fr-FR', { month: 'short' })}
                </p>
                {isRamadan && (
                  <span className="text-[9px] font-medium text-emerald-600">Ramadan</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Time Grid */}
        <div className="grid grid-cols-[50px_repeat(7,1fr)]">
          {TIME_SLOTS.map((timeSlot) => (
            <React.Fragment key={timeSlot}>
              {/* Time Label */}
              <div className="h-11 border-b border-r border-gray-100 px-1 py-1 flex items-start justify-center">
                <span className="text-[10px] text-gray-500 font-medium">{timeSlot}</span>
              </div>

              {/* Day Cells */}
              {DAYS.map((dayOfWeek) => {
                const cellEntries = getEntriesForDayAndTime(dayOfWeek, timeSlot);
                const today = isToday(dayOfWeek);
                const isRamadan = isRamadanDay(dayOfWeek);
                const dateForDay = getDateForDay(dayOfWeek);

                return (
                  <div
                    key={`${dayOfWeek}-${timeSlot}`}
                    className={`h-11 border-b border-l border-gray-100 relative ${
                      isRamadan
                        ? 'bg-emerald-50/30'
                        : today
                        ? 'bg-blue-50/30'
                        : ''
                    }`}
                  >
                    {/* Entries */}
                    {cellEntries.map((entry, entryIndex) => {
                      const entryHeight = getEntryHeight(entry, dateForDay);
                      const entryTopOffset = getEntryTopOffset(entry, dateForDay);
                      const { startTime, endTime } = getDisplayTimes(entry, dateForDay);
                      const professorName = entry.professor
                        ? `${entry.professor.firstName} ${entry.professor.lastName}`
                        : 'Professeur';

                      return (
                        <div
                          key={entry.id}
                          className="absolute left-0.5 right-0.5 overflow-hidden rounded-md border-l-3 shadow-sm hover:shadow-md transition-all cursor-pointer"
                          style={{
                            top: `${entryTopOffset}px`,
                            height: `${entryHeight - 2}px`,
                            borderLeftWidth: '3px',
                            borderLeftColor: entry.sessionType?.color || '#3B82F6',
                            backgroundColor: `${entry.sessionType?.color || '#3B82F6'}15`,
                            zIndex: 10 + entryIndex,
                          }}
                          title={`${entry.moduleElement?.name}\n${professorName}\n${entry.room?.name || 'Salle non définie'}`}
                        >
                          <div className="flex flex-col h-full p-1 overflow-hidden">
                            {/* Time */}
                            <div className="flex items-center gap-0.5 text-[9px] text-gray-500 flex-shrink-0">
                              <Clock size={8} />
                              <span>{startTime} - {endTime}</span>
                            </div>

                            {/* Module Name */}
                            <p className="font-semibold text-gray-900 text-[10px] truncate leading-tight">
                              {entry.moduleElement?.name || 'Matière'}
                            </p>

                            {/* Professor */}
                            {entry.professor && entryHeight > 50 && (
                              <div className="flex items-center gap-0.5 text-[9px] text-gray-600">
                                <User size={8} />
                                <span className="truncate">{professorName}</span>
                              </div>
                            )}

                            {/* Room */}
                            {entry.room && entryHeight > 70 && (
                              <div className="flex items-center gap-0.5 text-[9px] text-gray-500 mt-auto">
                                <MapPin size={8} />
                                <span className="truncate">{entry.room.name}</span>
                              </div>
                            )}

                            {/* Session Type Badge */}
                            {entry.sessionType && entryHeight > 85 && (
                              <div
                                className="inline-flex items-center text-[8px] font-medium px-1 py-0.5 rounded mt-0.5"
                                style={{
                                  backgroundColor: `${entry.sessionType.color || '#3B82F6'}25`,
                                  color: entry.sessionType.color || '#3B82F6',
                                }}
                              >
                                {entry.sessionType.name}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  // Render monthly calendar view
  const renderMonthlyView = () => {
    const firstDayOfMonth = new Date(currentMonth.year, currentMonth.month, 1);
    const lastDayOfMonth = new Date(currentMonth.year, currentMonth.month + 1, 0);
    const startDay = firstDayOfMonth.getDay() || 7;
    const daysInMonth = lastDayOfMonth.getDate();

    const calendarDays: (number | null)[] = [];

    for (let i = 1; i < startDay; i++) {
      calendarDays.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      calendarDays.push(i);
    }

    const getEntriesForDate = (day: number) => {
      const dateStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return entriesByDate.get(dateStr) || [];
    };

    const isCurrentMonthCheck = () => {
      const now = new Date();
      return now.getFullYear() === currentMonth.year && now.getMonth() === currentMonth.month;
    };

    const todayDate = new Date().getDate();

    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex-1">
        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
            <div key={day} className="p-2 text-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const dayEntries = day ? getEntriesForDate(day) : [];
            const isWeekend = (index % 7 === 5) || (index % 7 === 6);
            const today = isCurrentMonthCheck() && day === todayDate;

            return (
              <div
                key={index}
                className={`min-h-[80px] border-b border-r border-gray-100 p-1 ${
                  !day ? 'bg-gray-50' : isWeekend ? 'bg-gray-50/50' : ''
                } ${today ? 'bg-blue-50/50' : ''}`}
              >
                {day && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${
                      today ? 'text-blue-700 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center' : 'text-gray-700'
                    }`}>
                      {day}
                    </div>
                    <div className="space-y-0.5">
                      {dayEntries.slice(0, 3).map(({ entry, session }) => (
                        <div
                          key={entry.id}
                          className="text-[9px] px-1 py-0.5 rounded truncate"
                          style={{
                            backgroundColor: `${entry.sessionType?.color || '#3B82F6'}20`,
                            color: entry.sessionType?.color || '#3B82F6',
                          }}
                          title={`${entry.moduleElement?.name} - ${entry.professor?.firstName} ${entry.professor?.lastName}`}
                        >
                          {session.startTime} {entry.moduleElement?.name}
                        </div>
                      ))}
                      {dayEntries.length > 3 && (
                        <div className="text-[9px] text-gray-500 px-1">
                          +{dayEntries.length - 3} autres
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
            <Calendar className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Mon Emploi du Temps
            </h1>
            <p className="text-gray-500 text-sm">
              Votre planning de cours
            </p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('weekly')}
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all
              ${viewMode === 'weekly'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
          >
            <Grid3X3 size={14} />
            Semaine
          </button>
          <button
            onClick={() => setViewMode('monthly')}
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all
              ${viewMode === 'monthly'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
          >
            <CalendarDays size={14} />
            Mois
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mb-4 bg-white rounded-xl border border-gray-200 px-4 py-2">
        <button
          onClick={viewMode === 'weekly' ? handlePreviousWeek : handlePreviousMonth}
          className="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
        >
          <ChevronLeft size={18} />
          <span className="hidden sm:inline">{viewMode === 'weekly' ? 'Semaine précédente' : 'Mois précédent'}</span>
        </button>

        <div className="flex items-center gap-3">
          <span className="text-base font-semibold text-gray-900">
            {viewMode === 'weekly' ? formatWeekRange() : formatMonth()}
          </span>
          <button
            onClick={viewMode === 'weekly' ? handleToday : () => {
              const today = new Date();
              setCurrentMonth({ year: today.getFullYear(), month: today.getMonth() });
            }}
            className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            Aujourd'hui
          </button>
        </div>

        <button
          onClick={viewMode === 'weekly' ? handleNextWeek : handleNextMonth}
          className="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
        >
          <span className="hidden sm:inline">{viewMode === 'weekly' ? 'Semaine suivante' : 'Mois suivant'}</span>
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
          <span className="text-red-700 text-sm">{error}</span>
          <button
            onClick={loadSchedule}
            className="ml-auto text-red-600 hover:text-red-800 underline text-sm"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center flex-1 bg-white rounded-xl border border-gray-200">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Chargement de l'emploi du temps...</p>
            </div>
          </div>
        ) : viewMode === 'weekly' ? (
          renderWeeklyGrid()
        ) : (
          renderMonthlyView()
        )}
      </div>
    </div>
  );
};

export default MySchedulePage;
